import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Icon,
  ScrollArea,
  Spinner,
  Text,
} from "@salestrak/ui";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle } from "#app/components/page-header";
import { useApiKeys, useApiKeyMutations } from "#app/hooks/settings/use-api-key-mutations";
import {
  useWebhooks,
  useWebhookMutations,
  type WebhookItem,
} from "#app/hooks/settings/use-webhook-mutations";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { WebhookActionsMenu } from "#app/components/settings/webhook-actions-menu";
import { ApiKeyActionsMenu } from "#app/components/settings/api-key-actions-menu";
import { EntityTag } from "#app/components/entity";
import { getPermissionsLabel } from "#app/components/settings/permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { CORE_API_BASE } from "#app/lib/api-bases";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function ApiPage() {
  const { push } = useAppNavigate();

  const {
    webhooks,
    total: webhookTotal,
    isLoading: webhooksLoading,
    error: webhooksError,
  } = useWebhooks();
  const { deleteWebhook, toggleWebhookArchived } = useWebhookMutations();
  const {
    apiKeys,
    total: apiKeyTotal,
    isLoading: apiKeysLoading,
    error: apiKeysError,
  } = useApiKeys();
  const { revokeApiKey } = useApiKeyMutations();

  const [deletingWebhook, setDeletingWebhook] = useState<WebhookItem | null>(null);
  const [revokingApiKey, setRevokingApiKey] = useState<{ apiKeyId: string; name: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const isLoading = (webhooksLoading || apiKeysLoading) && webhooks.length === 0 && apiKeys.length === 0;
  const error = webhooksError || apiKeysError;

  // Seeds the GraphQL playground with a runnable sample query + variables.
  const playgroundUrl = useMemo(() => {
    const query = `query GetIssues($limit: Int!, $where: IssueWhereInput) {
  issues(limit: $limit, where: $where) {
    edges {
      node {
        issueId
        name
      }
    }
    total
  }
}`;
    const variables = JSON.stringify(
      { limit: 10, where: { search: "[Project Name]" } },
      null,
      2,
    );
    const params = new URLSearchParams({ query, variables });
    return `${CORE_API_BASE}/graphql?${params.toString()}`;
  }, []);

  async function handleDeleteWebhook() {
    if (!deletingWebhook) return;
    await deleteWebhook(deletingWebhook.webhookId);
  }

  async function handleRevokeApiKey() {
    if (!revokingApiKey) return;
    await revokeApiKey(revokingApiKey.apiKeyId);
  }

  if (isLoading) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (error && webhooks.length === 0 && apiKeys.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load API settings</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>API</PageHeaderTitle>
          <PageHeaderSubtitle>
            SalesTrak API offers a powerful, programmable interface to your data. Use it to build public or private apps, automate workflows, and create seamless integrations with SalesTrak.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <SettingsGroup label="GraphQL">
              <SettingsGroupRow
                label="GraphQL Playground"
                subtitle="Test your GraphQL queries and mutations."
              >
                <Button
                  render={
                    <Text
                      as="a"
                      href={playgroundUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    />
                  }
                  variant="ghost"
                  size="sm"
                >
                  <Icon name="external-link" size="md" />
                  Open Playground
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>

            {/* Webhooks */}
            <SettingsGroup
              label="Webhooks"
              subtitle="Webhooks enable you to receive HTTP requests automatically when a project is created, updated, or deleted."
            >
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {webhookTotal} {webhookTotal === 1 ? "webhook" : "webhooks"}
                </Text>
                <Button
                  aria-label="Create webhook"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => push("/settings/api/webhooks/create")}
                >
                  <Icon name="plus" size="md" />
                </Button>
              </SettingsGroupHeader>

              {webhooks.map((webhook: WebhookItem) => (
                <SettingsGroupRow
                  key={webhook.webhookId}
                  label={webhook.name}
                  subtitle={webhook.url}
                  leading={
                    <ColorSwatch
                      color={webhook.archived ? "grey" : "green"}
                      size="xs"
                      rounded="full"
                    />
                  }
                  onClick={() => push(`/settings/api/webhooks/${webhook.webhookId}`)}
                  actionsVisible={openMenuId === webhook.webhookId}
                >
                  <WebhookActionsMenu
                    archived={webhook.archived}
                    onOpenChange={(open) => setOpenMenuId(open ? webhook.webhookId : null)}
                    onEdit={() => push(`/settings/api/webhooks/${webhook.webhookId}/edit`)}
                    onToggleArchived={() => toggleWebhookArchived(webhook)}
                    onDelete={() => setDeletingWebhook(webhook)}
                  />
                </SettingsGroupRow>
              ))}
            </SettingsGroup>

            {/* Member API Keys */}
            <SettingsGroup
              label="Member API Keys"
              subtitle="Workspace members can generate API keys for personal use with the SalesTrak API."
            >
              <SettingsGroupHeader>
                <Text size="sm" weight="medium">
                  {apiKeyTotal} {apiKeyTotal === 1 ? "API key" : "API keys"}
                </Text>
                <Button
                  aria-label="Create API key"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => push("/settings/api/keys/create")}
                >
                  <Icon name="plus" size="md" />
                </Button>
              </SettingsGroupHeader>

              {apiKeys.map((key: any) => {
                const dateLabel = key.creationDate ? formatDate(key.creationDate) : null;
                return (
                  <SettingsGroupRow
                    key={key.apiKeyId}
                    label={key.name}
                    subtitle={`${getPermissionsLabel(key.permissions)}${dateLabel ? ` · ${dateLabel}` : ""}`}
                    leading={
                      <EntityTag
                        entity={{
                          kind: "user",
                          id: key.user?.userId ?? key.apiKeyId,
                          user: key.user ?? null,
                        }}
                        display="visual"
                        size="md"
                        interactive={false}
                      />
                    }
                    onClick={() => push(`/settings/api/keys/${key.apiKeyId}`)}
                    actionsVisible={openMenuId === key.apiKeyId}
                  >
                    <ApiKeyActionsMenu
                      onOpenChange={(open) => setOpenMenuId(open ? key.apiKeyId : null)}
                      onEdit={() => push(`/settings/api/keys/${key.apiKeyId}/edit`)}
                      onRevoke={() => setRevokingApiKey({ apiKeyId: key.apiKeyId, name: key.name })}
                    />
                  </SettingsGroupRow>
                );
              })}
            </SettingsGroup>
          </Box>
        </Box>
      </ScrollArea>

      <TrashConfirmDialog
        open={deletingWebhook !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingWebhook(null);
        }}
        title="Delete webhook?"
        description={`This will permanently delete the webhook for ${deletingWebhook?.url ?? ""}. This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteWebhook}
      />

      <TrashConfirmDialog
        open={revokingApiKey !== null}
        onOpenChange={(open) => {
          if (!open) setRevokingApiKey(null);
        }}
        title="Revoke API key?"
        description={`This will permanently revoke "${revokingApiKey?.name ?? ""}". Any applications using this key will lose access immediately.`}
        confirmLabel="Revoke"
        onConfirm={handleRevokeApiKey}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/api/")({
  component: withPermission("workspace.integrations", ApiPage),
});
